/**
 * Term Sheet — per-contract summary view.
 *
 * Reads from /api/contracts?portfolio_id=<id> (Firestore-backed). User picks
 * one contract from the strip and we render a curated commercial-terms view:
 *
 *   • Header — counterparty, project, term, status
 *   • KPIs   — Capacity, Buyer's Share, MEG, Settlement type, Reference node
 *   • Price schedule chart — the most important single artefact
 *   • Pricing details — reference node, region, curtailment strike, loss factor
 *   • Key dates — trade, effective, target COD, sunset, termination
 *   • Delay damages — per-day + cap
 *   • Obligations checklist — recurring commitments + cadence + status
 *   • Events timeline — one-off dated events with consequences
 *
 * Replaces the legacy PpaCard which read the old Postgres-typed contracts.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Stack, Chip, Skeleton, Alert, Tooltip,
  Card, CardContent, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoltIcon from '@mui/icons-material/Bolt';
import EventNoteIcon from '@mui/icons-material/EventNote';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { LineChart } from '@mui/x-charts/LineChart';
import axios from 'axios';

import { useThemeColors } from '../theme/ThemeModeContext';
import PpaWorkspaceTabs from './PpaWorkspaceTabs';
import ContractStatusChip from './ContractStatusChip';
import ErrorBoundary from './ErrorBoundary';
import AssetChip from './AssetChip';
// Contract expiry timeline — relocated here from the Documents tab so the
// term-sheet section owns the "when does each contract run / expire" view.
import ContractTimeline from './ContractTimeline';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// ── Formatting helpers ────────────────────────────────────────────────────────

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.valueOf())) return s;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtAud = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `A$${Math.round(n).toLocaleString('en-AU')}`;
};

const fmtAudPerMwh = (n) => (n == null ? '—' : `A$${Number(n).toFixed(2)}/MWh`);

const fmtPct = (n) => (n == null ? '—' : `${(n * 100).toFixed(0)}%`);

const fmtMwh = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('en-AU')} MWh`);

const fmtMw = (n) => (n == null ? '—' : `${Number(n).toFixed(0)} MW`);

const cadenceLabel = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly',
  annual: 'Annual', ad_hoc: 'Ad-hoc', continuous: 'Continuous',
};

const lgcTreatmentLabel = {
  bundled_in_fixed_price: 'Bundled in fixed price',
  separate_price:         'Separate price',
  none:                   'No LGC component',
};

const settlementLabel = {
  physical:      'Physical',
  financial_cfd: 'Financial CFD',
};

// ── Compact KPI tile ──────────────────────────────────────────────────────────

const KpiTile = ({ label, value, hint }) => (
  <Box sx={{
    flex: '1 1 0', minWidth: 130,
    p: 1.5, borderRadius: 1,
    bgcolor: 'rgba(217, 138, 43, 0.04)',
    border: '1px solid rgba(217, 138, 43, 0.10)',
  }}>
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary',
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'text.primary',
                      lineHeight: 1.2, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

// ── Section card wrapper ──────────────────────────────────────────────────────

// Every Section is wrapped in its own ErrorBoundary so a broken chart or
// row doesn't bring down the whole Term Sheet. The boundary name surfaces
// in the fallback so we know which section to fix.
const Section = ({ title, right, children }) => (
  <Card variant="outlined" sx={{ mb: 2, borderColor: 'divider', borderRadius: 1.5 }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary',
                          letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
        {right}
      </Stack>
      <ErrorBoundary name={title} compact>
        {children}
      </ErrorBoundary>
    </CardContent>
  </Card>
);

// ── KV grid for the "key terms at a glance" section ───────────────────────────

const KvRow = ({ k, v }) => (
  <Stack direction="row" spacing={2} sx={{ py: 0.6, borderBottom: '1px dashed', borderBottomColor: 'divider' }}>
    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', minWidth: 180 }}>{k}</Typography>
    <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
      {v ?? '—'}
    </Typography>
  </Stack>
);

// ── Status icon for obligations ──────────────────────────────────────────────

const obligationIcon = (status) => {
  switch (status) {
    case 'met':       return <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />;
    case 'overdue':   return <WarningAmberRoundedIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    case 'due_soon':  return <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#d4930d' }} />;
    case 'blocked':   return <WarningAmberRoundedIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    default:          return <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.secondary', opacity: 0.4, mt: '4px', ml: '4px' }} />;
  }
};

// ─────────────────────────────────────────────────────────────────────────────

export default function TermSheet() {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  const [contracts, setContracts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading]    = useState(true);
  const [error, setError]        = useState(null);

  const fetchContracts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = portfolioId ? { portfolio_id: portfolioId } : {};
      const resp = await axios.get(`${API_BASE_URL}/api/contracts`, { params });
      // Only PPAs render usefully here. Others (Cap, CIS, ISDA) show in the
      // Uploads tab and will get their own term-sheet variants later.
      const ppas = (resp.data.contracts || []).filter(c => c.contract_kind === 'PPA');
      setContracts(ppas);
      if (ppas.length && !selectedId) setSelectedId(ppas[0].contract_id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContracts(); /* eslint-disable-next-line */ }, [portfolioId]);

  const selected = useMemo(
    () => contracts.find(c => c.contract_id === selectedId) || contracts[0],
    [contracts, selectedId],
  );

  const ppa = selected?.terms?.ppa || {};

  // ── Price-schedule chart data ──────────────────────────────────────────────
  // The schedule is a list of contiguous rows. To make the line chart meaningful
  // we draw two data points per row (the price at period_start and at period_end)
  // so the line shows flat-then-step-flat behaviour rather than diagonals.
  const priceChartData = useMemo(() => {
    const rows = (ppa.price_schedule || []).slice()
      .sort((a, b) => (a.period_start || '').localeCompare(b.period_start || ''));
    const xs = [];
    const ys = [];
    rows.forEach(r => {
      if (!r.period_start || !r.period_end || r.price_aud_per_mwh == null) return;
      xs.push(new Date(r.period_start));
      ys.push(r.price_aud_per_mwh);
      xs.push(new Date(r.period_end));
      ys.push(r.price_aud_per_mwh);
    });
    return { xs, ys };
  }, [ppa.price_schedule]);

  // ── Loading / empty states ─────────────────────────────────────────────────
  if (loading && !contracts.length) {
    return (
      <Box>
        <PpaWorkspaceTabs />
        <Box>
          {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 1.5, borderRadius: 1 }} />)}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PpaWorkspaceTabs />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!contracts.length) {
    return (
      <Box>
        <PpaWorkspaceTabs />
        <Card variant="outlined" sx={{ p: 3, textAlign: 'center', borderColor: 'divider' }}>
          <DescriptionEmptyIcon />
          <Typography sx={{ mt: 1.5, fontSize: '0.95rem', fontWeight: 600 }}>No PPAs yet</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
            Upload a PPA in the <b>Uploads</b> tab. Once processing completes it will appear here.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PpaWorkspaceTabs />

      {/* Contract picker — chip-strip across the top.
          No `mt: 2` here — PpaWorkspaceTabs already provides `mb: 2`. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mr: 1, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Contract
        </Typography>
        {contracts.map(c => (
          <Chip
            key={c.contract_id}
            label={c.counterparty || c.source?.filename || c.contract_id.slice(0, 8)}
            size="small"
            onClick={() => setSelectedId(c.contract_id)}
            sx={{
              cursor: 'pointer',
              bgcolor: selectedId === c.contract_id ? 'rgba(217, 138, 43, 0.16)' : 'transparent',
              color:   selectedId === c.contract_id ? 'primary.main' : 'text.secondary',
              border: '1px solid',
              borderColor: selectedId === c.contract_id ? 'primary.main' : 'divider',
              fontWeight: selectedId === c.contract_id ? 700 : 500,
              fontSize: '0.78rem',
              '&:hover': { bgcolor: 'rgba(217, 138, 43, 0.10)' },
            }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchContracts}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      {/* Contract expiry timeline (moved here from the Documents tab).
          Clicking a bar selects that contract's term sheet below. Wrapped
          in an ErrorBoundary because the SVG path has historically crashed
          on contracts missing legacy date fields. */}
      {contracts.length > 0 && (
        <ErrorBoundary name="Contract timeline" compact>
          <Box sx={{ mb: 2 }}>
            <ContractTimeline
              contracts={contracts}
              onContractClick={(id) => setSelectedId(id)}
            />
          </Box>
        </ErrorBoundary>
      )}

      {selected && (
        <>
          {/* ── Header ───────────────────────────────────────────────── */}
          <Card variant="outlined" sx={{ mb: 2, borderColor: 'divider', borderRadius: 1.5 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <BoltIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'text.primary' }}>
                      {selected.counterparty || 'Unknown counterparty'}
                    </Typography>
                    <ContractStatusChip
                      status={selected.processing?.status === 'ready'
                        ? (selected.status || 'ready')
                        : selected.processing?.status}
                    />
                  </Stack>
                  <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary' }}>
                    {selected.project?.name || selected.source?.filename || ''}
                    {selected.project?.capacity_mw ? ` · ${fmtMw(selected.project.capacity_mw)} ${selected.project.location || ''}` : ''}
                  </Typography>
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Term
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtDate(selected.effective_date)} <Box component="span" sx={{ color: 'text.disabled' }}>→</Box> {fmtDate(selected.termination_date)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* ── KPI strip ─────────────────────────────────────────────── */}
          <Stack direction="row" spacing={1.25} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <KpiTile label="Capacity"        value={fmtMw(selected.project?.capacity_mw ?? ppa.contracted_mw)} />
            <KpiTile label="Buyer's Share"   value={fmtPct(ppa.buyers_share_pct)} hint={ppa.buyers_share_pct === 1 ? 'Full offtake' : 'Partial offtake'} />
            <KpiTile label="Min Expected Gen"value={fmtMwh(ppa.min_expected_generation_mwh_per_year)} hint={ppa.min_expected_generation_mwh_per_year ? 'per Contract Year' : null} />
            <KpiTile label="Settlement"      value={settlementLabel[ppa.settlement_type] || ppa.settlement_type || '—'} />
            <KpiTile label="Reference Node"  value={ppa.reference_node || '—'} hint={ppa.region} />
          </Stack>

          {/* ── Covered assets — the DUIDs the LLM matched against the portfolio,
              enriched with asset names by the backend. Names lead, DUID in
              brackets for traceability. This is the bridge between contract
              text and BigQuery generation data: Reconciliation joins these
              DUIDs against the metering feed for actual-vs-MEG comparisons. */}
          {(() => {
            // Prefer the enriched `assets` array from the backend (has name +
            // DUID); fall back to plain DUIDs if enrichment hasn't run.
            const assets = selected.assets?.length > 0
              ? selected.assets
              : (selected.asset_ids || []).map(d => ({ asset_id: d, name: null }));
            if (!assets.length) {
              return (
                <Section title="Covered assets">
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    No assets matched from this portfolio. Re-process the contract after adding the
                    covered asset to this portfolio, or edit the contract to set asset IDs manually.
                  </Typography>
                </Section>
              );
            }
            return (
              <Section title={`Covered assets (${assets.length})`}
                       right={<Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                         Used in Reconciliation + Revenue Mix calculations
                       </Typography>}>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {assets.map(a => <AssetChip key={a.asset_id} asset={a} />)}
                </Stack>
              </Section>
            );
          })()}

          {/* ── Price schedule chart ─────────────────────────────────── */}
          <Section
            title="Fixed Price schedule"
            right={
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {priceChartData.xs.length / 2} period{priceChartData.xs.length / 2 === 1 ? '' : 's'}
              </Typography>
            }
          >
            {priceChartData.xs.length > 0 ? (
              <Box sx={{ height: 240 }}>
                <LineChart
                  xAxis={[{ data: priceChartData.xs, scaleType: 'time',
                            valueFormatter: d => new Date(d).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }) }]}
                  yAxis={[{ valueFormatter: v => `A$${v}` }]}
                  series={[{ data: priceChartData.ys, color: colors?.primary || '#D98A2B',
                             showMark: false, curve: 'stepAfter',
                             valueFormatter: v => fmtAudPerMwh(v) }]}
                  grid={{ horizontal: true }}
                  margin={{ left: 60, right: 20, top: 10, bottom: 30 }}
                  height={230}
                />
              </Box>
            ) : ppa.strike_aud_per_mwh != null ? (
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}>
                {fmtAudPerMwh(ppa.strike_aud_per_mwh)} <Box component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>(flat strike, no schedule extracted)</Box>
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontStyle: 'italic' }}>
                No fixed price found in extraction. Re-process or edit manually.
              </Typography>
            )}

            {(ppa.price_schedule || []).length > 0 && (
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Period</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Kind</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>A$ / MWh</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(ppa.price_schedule || []).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(r.period_start)} → {fmtDate(r.period_end)}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{r.kind || 'bundled'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        A${Number(r.price_aud_per_mwh).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>

          {/* ── Two columns: key terms + key dates ────────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Section title="Pricing & Settlement">
              <KvRow k="Settlement type"     v={settlementLabel[ppa.settlement_type] || ppa.settlement_type} />
              <KvRow k="Offtake structure"   v={ppa.offtake} />
              <KvRow k="Reference node"      v={ppa.reference_node} />
              <KvRow k="Region"              v={ppa.region} />
              <KvRow k="Curtailment strike"  v={ppa.curtailment_strike_aud_per_mwh != null ? fmtAudPerMwh(ppa.curtailment_strike_aud_per_mwh) : null} />
              <KvRow k="Loss factor source"  v={ppa.loss_factor_source} />
              <KvRow k="LGCs"                v={lgcTreatmentLabel[ppa.lgc_treatment] || ppa.lgc_treatment} />
              {ppa.lgc_price_aud_each != null && (
                <KvRow k="LGC price"           v={`A$${ppa.lgc_price_aud_each}/LGC`} />
              )}
            </Section>

            <Section title="Key dates">
              <KvRow k="Trade date"          v={fmtDate(selected.trade_date)} />
              <KvRow k="Effective date"      v={fmtDate(selected.effective_date)} />
              <KvRow k="Target COD"          v={fmtDate(selected.target_cod)} />
              <KvRow k="COD sunset"          v={fmtDate(selected.cod_sunset)} />
              <KvRow k="Termination date"    v={fmtDate(selected.termination_date)} />
              {ppa.delay_damages && (
                <>
                  <KvRow k="Delay damages"     v={ppa.delay_damages.per_day_aud != null ? `${fmtAud(ppa.delay_damages.per_day_aud)} / day` : null} />
                  <KvRow k="Delay damages cap" v={ppa.delay_damages.cap_aud != null ? fmtAud(ppa.delay_damages.cap_aud) : null} />
                </>
              )}
            </Section>
          </Box>

          {/* ── Obligations checklist ────────────────────────────────── */}
          {(selected.obligations || []).length > 0 && (
            <Section title={`Obligations (${selected.obligations.length})`}>
              {selected.obligations.map((o, i) => (
                <Stack key={`${o.code}-${i}`} direction="row" alignItems="center" spacing={1.5}
                       sx={{ py: 0.75, borderBottom: i === selected.obligations.length - 1 ? 'none' : '1px solid', borderBottomColor: 'divider' }}>
                  {obligationIcon(o.status)}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                      {o.label}
                    </Typography>
                    {o.description && (
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                        {o.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={cadenceLabel[o.cadence] || o.cadence || '—'}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 22, bgcolor: 'rgba(217, 138, 43,0.08)', color: 'primary.main' }}
                  />
                  {o.clause_ref && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontFamily: "'JetBrains Mono', monospace" }}>
                      {o.clause_ref}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Section>
          )}

          {/* ── Events timeline ──────────────────────────────────────── */}
          {(selected.events || []).length > 0 && (
            <Section title={`Dated events (${selected.events.length})`}>
              {selected.events.map((e, i) => (
                <Stack key={`${e.code}-${i}`} direction="row" alignItems="center" spacing={2}
                       sx={{ py: 0.75, borderBottom: i === selected.events.length - 1 ? 'none' : '1px solid', borderBottomColor: 'divider' }}>
                  <EventNoteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>{e.label}</Typography>
                    {e.consequence && (
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        Consequence: {e.consequence.kind || JSON.stringify(e.consequence)}
                        {e.consequence.per_day_aud ? ` · ${fmtAud(e.consequence.per_day_aud)}/day` : ''}
                        {e.consequence.cap_aud     ? ` · cap ${fmtAud(e.consequence.cap_aud)}` : ''}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtDate(e.due_date)}
                  </Typography>
                </Stack>
              ))}
            </Section>
          )}
        </>
      )}
    </Box>
  );
}

// Tiny helper SVG icon used in the empty state.
const DescriptionEmptyIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.4 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);
